import { Icon, ListItem } from 'canoe';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import React, { useCallback } from 'react';

import Hubspot from '!!react-svg-loader!./assets/Hubspot.svg';
import Segment from '!!react-svg-loader!./assets/Segment.svg';
import { AutocompleteSelect } from '@candu-ai/analytics/ui';
import { TraitEnglishNames, TraitTypes } from '@candu-ai/segments/types';
import { RenderItemProps } from 'canoe/components/List';
import { stripNameDifferentiator } from 'dashboard/pages/Segment/components/DefinitionForm/nameDifferentiatorUtils';
import { SelectInputWrapper } from 'dashboard/pages/Segment/components/DefinitionFormFields/DefinitionFormComponents';
import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { TraitQueryData, TraitQueryItem } from './UserPropertyRule';

const SourceIconMappings = {
  Hubspot,
  Segment,
  Candu: undefined,
} as { [key: string]: any | undefined };

type TraitSelectItem = {
  trait: string;
  value: string;
  label: string;
  source: string;
  search: string;
};

interface TraitSelectProps {
  name: string;
  readOnly?: boolean;
  handleChange: (selected: TraitQueryItem) => void;
  onQuerySuccess?: (data: TraitQueryData) => void;
}

export const TraitSelect = ({ name, readOnly, handleChange, onQuerySuccess }: TraitSelectProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const apiQuery = useCallback(
    () => ({
      query: {
        finder: 'byUserTraits',
      },
    }),
    [],
  );

  const getFormattedResults = useCallback((results: TraitQueryData, searchValue: string) => {
    const rawName = get(values, name);
    const traitName =
      rawName && typeof rawName === 'string' ? stripNameDifferentiator(rawName) : undefined;

    // if trait name doesn't exist in the query results, treat it as a custom value
    const hasCustomTrait =
      !!traitName && !results.find(({ name: itemName }) => itemName === traitName);

    return [
      ...(hasCustomTrait
        ? [
            {
              trait: JSON.stringify({ name: traitName, traitType: TraitTypes.String }),
              value: `${traitName}_differentiator(${TraitTypes.String})`,
              label: `${traitName} (${TraitEnglishNames[TraitTypes.String]})`,
              source: '',
              search: `${traitName} ${TraitEnglishNames[TraitTypes.String]}`,
            },
          ]
        : []),
      ...(results
        .reduce((acc, trait) => {
          // remove duplicate entries that match both trait name and type
          const exists = acc.find(
            (existingTrait: TraitQueryItem) =>
              existingTrait.name === trait.name && existingTrait.traitType === trait.traitType,
          );

          if (exists) {
            return acc;
          }

          return [...acc, trait];
        }, [])
        .map((result) => {
          const friendlyTypeName = TraitEnglishNames[result.traitType];
          const traitAndTypeValue = JSON.stringify(result);

          return {
            trait: traitAndTypeValue,
            value: `${result.name}_differentiator(${result.traitType})`,
            label: `${result.name} (${friendlyTypeName})`,
            source: result.source,
            search: `${result.name} ${friendlyTypeName} ${result.source}`,
          };
        })
        .filter(({ search }) => search.toLowerCase().includes(searchValue.toLowerCase())) || []),
    ];
  }, []);

  const onChange = useCallback(
    (item: TraitSelectItem) => {
      let parsedValue;

      if (item.trait) {
        parsedValue = JSON.parse(item.trait);
      } else {
        // for new custom values input through search
        parsedValue = {
          name: stripNameDifferentiator(item.value),
          traitType: TraitTypes.String,
        };
      }

      handleChange(parsedValue);
    },
    [handleChange],
  );

  const renderItem = useCallback(({ item, ...itemProps }: RenderItemProps<TraitSelectItem>) => {
    return (
      <ListItem
        suffix={
          item.source && SourceIconMappings[item.source] ? (
            <Icon title={item.source} svg={SourceIconMappings[item.source]} />
          ) : undefined
        }
        {...itemProps}
      >
        {stripNameDifferentiator(item?.label || '')}
      </ListItem>
    );
  }, []);

  return (
    <AutocompleteSelect<TraitSelectItem, TraitQueryData, DefinitionFormikValues>
      name={name}
      apiQuery={apiQuery}
      onQuerySuccess={onQuerySuccess}
      readOnly={readOnly}
      selectFirstByDefault
      placeholder="Choose operator"
      emptyPlaceholder="You don't have any user traits yet"
      getFormattedResults={getFormattedResults}
      onChange={onChange}
      renderItem={renderItem}
      InputWrapper={SelectInputWrapper}
      data-cy="segment-definition-selector"
      values={values}
      setFieldValue={setFieldValue}
      allowCustomValues
    />
  );
};
